import { GetStaticPropsContext } from 'next';

import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import {
  ClientSuccessStoriesSection,
  StaticClientSuccessStoriesSectionProps,
  transformStaticClientSuccessStoriesSectionProps,
} from '~/features/page-template/templates/enterprise-overview/components/client-success-section';
import { FooterBannerSection } from '~/features/page-template/templates/enterprise-overview/components/footer-banner-section';
import { EnterpriseOverviewHeroSection } from '~/features/page-template/templates/enterprise-overview/components/hero-section';
import {
  LogosCarousel,
  StaticLogosCarouselProps,
  transformStaticLogosCarouselProps,
} from '~/features/page-template/templates/enterprise-overview/components/logos-carousel';
import { OutcomesSection } from '~/features/page-template/templates/enterprise-overview/components/outcomes-section';
import { SchoolsListSubsection } from '~/features/page-template/templates/enterprise-overview/components/schools-list-subsection';
import { TQSection } from '~/features/page-template/templates/enterprise-overview/components/tq-section';
import { UdacityPlatformSection } from '~/features/page-template/templates/enterprise-overview/components/udacity-platform-section';

export default function EnterpriseOverviewPage({
  page,
  staticLogosCarouselProps,
  staticClientSuccessStoriesSectionProps,
}: {
  page: Page;
  staticLogosCarouselProps: StaticLogosCarouselProps;
  staticClientSuccessStoriesSectionProps: StaticClientSuccessStoriesSectionProps;
}) {
  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <EnterpriseOverviewHeroSection />
        <LogosCarousel {...staticLogosCarouselProps} />
        <ClientSuccessStoriesSection {...staticClientSuccessStoriesSectionProps} />
        <OutcomesSection />
        <UdacityPlatformSection />
        <TQSection footerSlot={<SchoolsListSubsection />} />
        <FooterBannerSection />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, staticLogosCarouselProps, staticClientSuccessStoriesSectionProps] = await Promise.all([
    getPage('/enterprise/overview'),
    transformStaticLogosCarouselProps(),
    transformStaticClientSuccessStoriesSectionProps(),
  ]);

  return {
    props: {
      page,
      staticLogosCarouselProps,
      staticClientSuccessStoriesSectionProps,
    },
  };
}
